import moment from "moment";
import { ClassifierType } from "models/ClassifierModel";
import { SlimLLMVariable } from "models/LLMs/LLMVariable";
import {
    conditionExpressionByPhrase,
    ConditionExpressionPhrase,
    orderedConditionExpressions,
} from "models/LLMs/LogicChaining/Conditions/ConditionExpressionMeta";
import type { ModelChainNode } from "models/LLMs/ModelChain";
import { LLMVariableValueOption } from "../Components/LLMVariableValueField";

/**
 * The shapes the Chain source section and the Model chain map are drawn from,
 * and the pure helpers that derive their display text. `ChainSourceStore` builds
 * these out of the platform's variables and the model-chain API; everything here
 * is free of store state so it can be read, and tested, on its own.
 */

/**
 * Stored as the spoken form ("does not equal") so a gate reads as a sentence.
 * Saving converts it back to the `ConditionExpressionType` the executor
 * evaluates, with `conditionExpressionByPhrase`.
 */
export type ChainConditionOperator = ConditionExpressionPhrase;

/** `Exists` and `DoesNotExist` test the variable alone, with nothing to compare to. */
export function operatorTakesValue(operator: ChainConditionOperator): boolean {
    return conditionExpressionByPhrase[operator].takesValue;
}

export const allConditionOperators: ChainConditionOperator[] =
    orderedConditionExpressions.map((expression) => expression.phrase);

/** Every operator but the substring ones, which need free text to compare in. */
export const wholeValueConditionOperators: ChainConditionOperator[] =
    orderedConditionExpressions
        .filter((expression) => !expression.isStringComparison)
        .map((expression) => expression.phrase);

export type ChainModelKind = "ml" | "llm";

/** How a parent's origin reads wherever a model stands in for its own output. */
export function modelKindLabel(kind: ChainModelKind): string {
    return kind === "ml" ? "Core AI model" : "LLM model";
}

export function getModelKind(node?: ModelChainNode): ChainModelKind {
    return node?.classifierTypeName === ClassifierType.LLM ? "llm" : "ml";
}

const maxListedOutputs = 3;

/**
 * A model with a structured output can emit many deeply named fields. List a few and count the
 * rest; the full set is available in a tooltip wherever this is shown.
 */
export function summarizeOutputNames(names: string[]): string {
    if (names.length <= maxListedOutputs) return names.join(", ");

    return `${names.slice(0, maxListedOutputs).join(", ")} +${names.length - maxListedOutputs
        } more`;
}

/**
 * Labels the parent implementation a relation records, by when that
 * implementation was created, as `YYYYMMDD-HHMM`. A save writes a new row rather
 * than editing the last one, so creation time is fixed for that version, while
 * modified time moves on publish and unpublish.
 */
export function formatVersionLabel(
    createdOn?: string | null,
): string | undefined {
    if (!createdOn) return undefined;

    const created = moment.utc(createdOn);
    if (!created.isValid()) return undefined;

    return created.local().format("YYYYMMDD-HHmm");
}

/** One output a parent model emits. */
export interface ChainSourceVariable {
    /** Fully qualified variable name, e.g. `__amd.ml_models.ML-SentimentV2.Beginning`. */
    name: string;
    /** The platform's display name for the variable. */
    label: string;
    /** The variable name without its group prefix, used as the input token. */
    shortName: string;
    /**
     * The variable as the platform published it, so the run condition can render
     * its value with the same rules the metadata filters use.
     */
    source: SlimLLMVariable;
    /**
     * Values the output can be compared against, empty when it is free text.
     * A value carries a label separately because some variables, contact type in
     * particular, publish a friendly label alongside the stored value.
     */
    values: LLMVariableValueOption[];
}

export interface ChainSourceModel {
    /**
     * The classifier behind the model. Keyed on the classifier id rather than the
     * variable name so a rename does not break the reference, and so a Core AI
     * model and an LLM model that share a name stay distinct.
     */
    id: string;
    name: string;
    kind: ChainModelKind;
    /**
     * How far above this model the parent sits. Every direct parent is one level
     * up, and parents that share a level are peers whose gates all have to pass.
     */
    level: number;
    /** Outputs the model emits. More than one for models such as sentiment. */
    variables: ChainSourceVariable[];
    /** True when the model only runs on a subset of conversations. */
    hasComputeRestrictions?: boolean;
    /** The exact parent implementation pinned by the relation. */
    implementationId?: string;
    /** That implementation's version, labelled by `formatVersionLabel`. */
    versionLabel?: string;
}

export interface ChainDependentModel {
    id: string;
    name: string;
    kind: ChainModelKind;
    /** Short names of this model's outputs the dependent actually reads. */
    consumes: string[];
}

/**
 * A model somewhere in the chain around this one. Only direct parents are
 * editable here, so anything further out carries just enough to be drawn.
 */
export interface ChainGraphModel {
    id: string;
    name: string;
    kind: ChainModelKind;
    /** Distance from this model. 1 is a direct parent or child. */
    level: number;
    isPublished: boolean;
    /** Version of the implementation the connecting relation records. */
    versionLabel?: string;
}

export interface ChainGraphLevel {
    level: number;
    models: ChainGraphModel[];
}

/** A step whose prompt references a parent's outputs. */
export interface ChainPromptReference {
    stepId: string;
    stepName: string;
    /** Short names of the parent outputs the prompt references. */
    variables: string[];
}

/** A model referenced by a complete prompt token but not yet declared as a parent. */
export interface UnparentedPromptModelReference {
    model: ChainSourceModel;
    /** Fully qualified variables referenced from this model. */
    variables: string[];
}

/** A step with IF/THEN action conditions testing a parent's outputs. */
export interface ChainStepConditionReference extends ChainPromptReference {
    /**
     * True when an action tests nothing but this parent, so removing its
     * conditions leaves it with nothing to fire on.
     */
    losesWholeAction: boolean;
}

export interface ChainRunCondition {
    /** Matches the persisted execute condition so multiple gates can share a parent. */
    id: string;
    /** Classifier id of the parent this condition gates on. */
    parentId: string;
    /** Fully qualified name of the parent variable being compared. */
    variable: string;
    operator: ChainConditionOperator;
    /**
     * Undefined until a value is chosen, which is where every gate starts. Once
     * an operator that compares values is picked, the step will not validate
     * until this is filled in.
     */
    value?: string;
}

export interface ChainDeclaredInput {
    name: string;
    /** Where the input comes from, e.g. `source` or `from Contact Type`. */
    origin: string;
    /** Parent the input is derived from, if any. */
    parentId?: string;
    /** Fully qualified name of the parent variable behind the input, if any. */
    variableName?: string;
}

/**
 * The inputs one source contributes. A parent with a structured output declares
 * several at once, so they are listed together under the model they came from
 * rather than repeating the model on every input.
 */
export interface ChainDeclaredInputGroup {
    /** Stable key for rendering. */
    key: string;
    /** Leading text: the input itself, or the model contributing the inputs. */
    name: string;
    /** Trailing text: where it came from, or the outputs it contributes. */
    detail: string;
    /** Every output, for a tooltip when `detail` had to summarize them. */
    fullDetail?: string;
    /** Parent the inputs come from, if any. */
    parentId?: string;
}

/**
 * The model name shared by every variable a classifier backs. Sentiment emits
 * `<name>.Beginning` and `<name>.Ending`, so the name is their common prefix;
 * for a single variable it is the whole name.
 */
export function sharedModelName(names: string[]): string {
    if (names.length === 1) return names[0];

    const segmented = names.map((name) => name.split("."));
    const shared: string[] = [];

    for (let index = 0; index < segmented[0].length; index++) {
        const segment = segmented[0][index];
        if (!segmented.every((segments) => segments[index] === segment)) break;
        shared.push(segment);
    }

    return shared.length > 0 ? shared.join(".") : names[0];
}
